import type { CampaignSummary } from "@/types/campaign";
import {
  CAMPAIGN_TYPE_LABELS,
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_COLORS,
} from "@/types/campaign";

interface Props {
  campaign: CampaignSummary;
  onSelect: (c: CampaignSummary) => void;
}

export default function CampaignCard({ campaign, onSelect }: Props) {
  const statusLabel = CAMPAIGN_STATUS_LABELS[campaign.status] ?? campaign.status;
  const statusColor = CAMPAIGN_STATUS_COLORS[campaign.status] ?? "bg-gray-100 text-gray-600";
  const typeLabel = CAMPAIGN_TYPE_LABELS[campaign.campaignType] ?? campaign.campaignType;

  const progressPct = Math.min(100, Math.round(campaign.progressPercent));

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition cursor-pointer"
      onClick={() => onSelect(campaign)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-foreground-500">{campaign.code}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
              {statusLabel}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 truncate">{campaign.name}</h3>
          <p className="text-xs text-foreground-500 mt-0.5">{typeLabel}</p>
        </div>
      </div>

      <div className="text-xs text-foreground-500 mb-3">
        <span>{campaign.projectName}</span>
        {campaign.areaPilotoName && (
          <span className="ml-1">/ {campaign.areaPilotoName}</span>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-foreground-600">
            {campaign.registeredTreeCount}
            {campaign.targetTreeCount ? ` / ${campaign.targetTreeCount}` : ""} árvores
          </span>
          <span className="font-medium text-foreground-700">{progressPct}%</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <div className="font-semibold text-foreground-700">{campaign.memberCount}</div>
          <div className="text-foreground-400">Membros</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-foreground-700">{campaign.quadraCount}</div>
          <div className="text-foreground-400">Quadras</div>
        </div>
        <div className="text-center">
          <div className="font-semibold text-foreground-700">{campaign.quadrasConcluidas}</div>
          <div className="text-foreground-400">Concluídas</div>
        </div>
      </div>

      {/* Dates */}
      {(campaign.startDate || campaign.endDate) && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-foreground-400 flex gap-3">
          {campaign.startDate && (
            <span>
              <i className="ri-calendar-line mr-1" />
              {new Date(campaign.startDate).toLocaleDateString("pt-BR")}
            </span>
          )}
          {campaign.endDate && (
            <span>
              <i className="ri-calendar-check-line mr-1" />
              {new Date(campaign.endDate).toLocaleDateString("pt-BR")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
